import SoundVars from "../../const/SoundVars";
import PointerEvents from "../../../src/const/PointerEvents";
import SoundController from "../../utils/SoundController";
import Layout from "../../utils/Layout";
import { TweenMax } from "gsap";
import TextField from "./TextField";
import { IObjectConfig } from "../../baseConfigs/layoutConfigShema";
import BaseView from "../BaseView";
import App from '../../App';
import Assets from '../../utils/Assets';
import { UIConfigType } from '../../utils/ConfigType';

/**
 * Basic button with changeable skins based on state
 */

class Button extends BaseView {
    public dir: number;
    protected down: boolean;
    protected over: boolean;
    public state: string;
    public text: TextField;
    protected animateScale: boolean;
    protected textFieldBound: IObjectConfig;
    protected downSndId: string;
    public butImg: PIXI.Sprite;
    protected skinMap: any; // TODO
    protected skins: IButtonSkin;
    protected _value:string;


    constructor(skin: IButtonSkin) {
        super();
        // TODO set default
        this.interactive = true;
        this.state = "UP";
        this.skins = skin;

        this.butImg = this.assets.getSprite(skin.up);
        this.addChild(this.butImg);
        this.setSkins(skin);

        this.down = false;
        this.over = false;

        this.setEventListeners();
    }

    public setHitArea(buttonName: string) {
        let config = App.assets.getConfig("uiConfig") as UIConfigType;
        let buttonHitArea = config.hitArea?.[buttonName] as UIConfigType["hitArea"][""];

        if (buttonHitArea) {
            switch (buttonHitArea.shape) {
                case "circle":
                    this.hitArea = new PIXI.Circle(
                        this.butImg.x,
                        this.butImg.y,
                        this.butImg.width * buttonHitArea.radius
                    );
                    break;

                case "triangle": {
                    let butX =
                        this.butImg.x -
                        this.butImg.width * this.butImg.anchor.x;
                    let butY =
                        this.butImg.y -
                        this.butImg.height * this.butImg.anchor.y;
                    let butWidth = this.butImg.width;
                    let butHeight = this.butImg.height;

                    if (buttonHitArea.xOffset) {
                        butX += this.butImg.width * buttonHitArea.xOffset;
                    }
                    if (buttonHitArea.yOffset) {
                        butY += this.butImg.height * buttonHitArea.yOffset;
                    }
                    if (buttonHitArea.widthOffset) {
                        butWidth += this.butImg.width * buttonHitArea.widthOffset;
                    }
                    if (buttonHitArea.heightOffset) {
                        butHeight += this.butImg.height * buttonHitArea.heightOffset;
                    }

                    if (buttonHitArea.direction == "right") {
                        this.hitArea = new PIXI.Polygon([
                            new PIXI.Point(butX, butY),
                            new PIXI.Point(butX, butY + butHeight),
                            new PIXI.Point(butX + butWidth, butY + butHeight * 0.5),
                        ]);
                    } else {
                        this.hitArea = new PIXI.Polygon([
                            new PIXI.Point(butX + butWidth, butY),
                            new PIXI.Point(butX + butWidth, butY + butHeight),
                            new PIXI.Point(butX, butY + butHeight * 0.5),
                        ]);
                    }
                }
            }
        }
    }

    /**
     * Create an exact replica of current button at a  new position
     * @param cloneX absolute coordinate x of new button
     * @param cloneY absolute coordinate y of new button
     */
    public clone(cloneX: number, cloneY: number): Button {
        const btn = new Button(this.skins);
        btn.butImg.x = Layout.scalePixels(cloneX);
        btn.butImg.y = Layout.scalePixels(cloneY);

        btn.setAnchor(this.butImg.anchor.x, this.butImg.anchor.y);
        Layout.testSetWidth(btn,Layout.getWidth(this));

        if (this.textFieldBound) {
            btn.textFieldBound = this.repositionBounds(cloneX, cloneY);
        }

        if (this.text) {
            const style: PIXI.TextStyleOptions = this.text.initStyle;
            const tf = new TextField(this.text.text, style, {
                    bound: this.repositionBounds(cloneX, cloneY),
                    anchor: this.butImg.anchor
            }
            );
            btn.addLabelTF(tf);
        }

        return btn;
    }

    public hide(){
        this.visible = false;
        if (this.text) {
            this.text.visible = false;
        }
    }

    public show(){
        this.visible = true;
        if (this.text) {
            this.text.visible = true;
        }
    }

    public changeBound(newBound: IObjectConfig) {
        if (this.text) {
            this.text.bound = {
                x: newBound.x,
                y: newBound.y,
                width: newBound.width,
                height: newBound.height
            };
            this.text.applyBound();
        }
    }

    /**
     * Recalculate baunds position
     * @param newX absolute coordinate x of button
     * @param newY absolute coordinate y of button
     */
    protected repositionBounds(newX: number, newY: number): IObjectConfig {
        if (this.text) {
            const offsetX = newX - Layout.getX(this.butImg);
            const offsetY = newY - Layout.getY(this.butImg);
            return {
                width: this.text.bound.width,
                height: this.text.bound.height,
                x: this.text.bound.x + offsetX,
                y: this.text.bound.y + offsetY
            };
        }
        return null;
    }

    protected setEventListeners() {
        // this.on('removed', this.destroy, this);
        this.on(PointerEvents.OVER, this.onMouseOver, this);
        this.on(PointerEvents.ROLLOUT, this.onMouseOut, this);
        this.on(PointerEvents.DOWN, this.onMouseDown, this);
    }

    /**
     * Set position and scale anchor point
     */
    public setAnchor(x: number = 0, y: number = x) {
        this.butImg.anchor.set(x, y);
    }

    /**
     * Set the click sound for the button
     */
    public setSound(sndId: string) {
        this.downSndId = sndId;
    }

    /**
     * Over state handler
     */
    protected onMouseOver() {
        this.state = "OVER";
        this.stateUpdated();
        this.over = true;
        // this.emit(C_PointerEvt.BUT_ROLLOVER);
    }

    /**
     * Out state handler
     */
    protected onMouseOut() {
        this.state = "UP";
        this.stateUpdated();
        this.over = false;
        // this.emit(C_PointerEvt.BUT_ROLLOUT);
    }

    /**
     * Down state handler
     */
    protected onMouseDown() {
        this.playInteractionSound();
        this.state = "DOWN";
        this.down = true;
        this.stateUpdated();
        document.addEventListener(PointerEvents.UP, () => {
            this.onMouseUp();
        }, {once: true});
    }

    /**
     * Up state handler
     */
    protected onMouseUp() {
        if (this.over) {
            this.state = "OVER";
        } else {
            this.state = "UP";
        }
        this.stateUpdated();
        this.down = false;
    }

    /**
     * Set if the button is enabled/interactive
     */
    public setEnabled(enabled: boolean) {
        super.setEnabled(enabled);

        if (enabled) {
            this.state = this.over ? "OVER" : "UP";
        } else {
            this.down = false;
            this.state = "DISABLED";
        }

        this.stateUpdated();
    }

    /**
     * Changes the skins of the button
     * If it has to be done probably there is a need for a different button instance
     */
    public setSkins(skin: IButtonSkin) {
        this.skinMap =
            {
                "UP": skin.up,
                "OVER": skin.over || skin.up,
                "DOWN": skin.down || skin.up,
                "DISABLED": skin.off || skin.up
            };

        this.stateUpdated();
    }

    /**
     * Add a text label
     */
    public addLabelTF(tf: TextField, bounds?: IObjectConfig)
    {
        if (!bounds) {
            bounds = {
                x: Layout.getX(this.butImg),
                y: Layout.getY(this.butImg),
                width: Layout.getWidth(this.butImg) *  80 / 100,
                height: Layout.getHeight(this.butImg) * 80 / 100
            };
            tf.anchor.set(this.butImg.anchor.x, this.butImg.anchor.y);
            tf.boundAnchor = {x: (0.5 - this.butImg.anchor.x), y:  (0.5 - this.butImg.anchor.y)};
            tf.isLabel = true;
        }
        tf.bound = bounds;
        tf.applyBound();
        this.addChild(tf);
        this.text = tf;
    }

    getValue(): string {
        let txtVal = null;
        if (this.text) {
            txtVal = this.text.text;
        }
        // return null;
        return this._value || txtVal;
    }

    public setValue(value: string,formatedValue?:string) {
        this._value = value;
        if (this.text) {
            this.text.text = formatedValue || value;
        }
    }

    protected stateUpdated() {
        const initScale = this.butImg.scale.x;
        this.butImg.texture = this.getSkin();
        this.butImg.scale.set(initScale);

        if (this.animateScale) {
            const scale = 1 + Number(!this.over) * .25;
            TweenMax.to(this.butImg.scale, .075, {x: scale, y: scale, overwrite: 1});
        }
    }

    getSkin():PIXI.Texture{
        let texture = this.assets.getTexture(this.skinMap[this.state]);
        if(!texture) {
            texture = this.assets.getTexture(this.skinMap["UP"]);
        }

        return texture;
    }

    // TODO CHECK IF NEEDED
    public setAnimateScale(val: boolean) {
        this.animateScale = val;
        if (val) {
            this.setAnchor(.5);
        } else {
            this.setAnchor(0);
        }
    }

    protected playInteractionSound() {
        if (this.downSndId) {
            SoundController.playSound(this.downSndId);
        }
    }


}

export interface IButtonSkin {
    up: string;
    over?: string,
    down?: string,
    off?: string,
}


export default Button;
